import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const AGENTS = [
  { id: "projectLeader",  name: "Líder de Proyecto", icon: "⭐" },
  { id: "strategist",     name: "Estratega",     icon: "🎯" },
  { id: "onboarding",     name: "Onboarding",    icon: "🔍" },
  { id: "copywriter",     name: "Copywriter",    icon: "✍️" },
  { id: "contentPlanner", name: "Planificador",  icon: "📅" },
  { id: "adsSpecialist",  name: "Ads",           icon: "📢" },
  { id: "seoAnalyst",     name: "SEO",           icon: "🔎" },
  { id: "reportAnalyst",  name: "Reportes",      icon: "📊" },
  { id: "upselling",      name: "Upselling",     icon: "💡" },
  { id: "competitive",    name: "Competencia",   icon: "🏆" },
  { id: "qa",             name: "QA",            icon: "✅" },
  { id: "uxWriter",       name: "UX Writer",     icon: "🖊️" },
  { id: "meetingMinutes", name: "Minutas",       icon: "🗒️" },
  { id: "pm",            name: "PM",            icon: "📬" },
];

const LEADER_NAV = [
  { path: "/semaforo", label: "Semáforo", icon: "🚦" },
];

function roleLabel(role) {
  return { super_admin: "Super Admin", team_leader: "Team Leader", member: "Member" }[role] || role;
}

export default function Layout() {
  const { user, logout, isSuperAdmin, isTeamLeader } = useAuth();
  const hasModule = (name) => user?.modules?.includes(name);
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const logoSrc = theme === "light" ? "/logo-light.png" : "/logo.png";

  // Close mobile drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navContent = (onClose, col = false) => (
    <>
      <div style={{ marginBottom: 16 }}>
        {!col && <SectionLabel>Principal</SectionLabel>}
        <NavItem to="/" icon="⚡" label="Dashboard" end onClose={onClose} collapsed={col} />
        <NavItem to="/clients" icon="👥" label="Clientes" onClose={onClose} collapsed={col} />
        {isSuperAdmin && (
          <NavItem to="/director" icon="🎬" label="Director" onClose={onClose} collapsed={col} />
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        {!col && <SectionLabel>Agentes</SectionLabel>}
        {AGENTS.map(a => (
          <NavItem key={a.id} to={`/agents/${a.id}`} icon={a.icon} label={a.name} onClose={onClose} collapsed={col} />
        ))}
      </div>
      {isTeamLeader && (
        <div style={{ marginBottom: 16 }}>
          {!col && <SectionLabel>Equipo</SectionLabel>}
          <NavItem to="/semaforo" icon="🚦" label="Semáforo" onClose={onClose} collapsed={col} />
        </div>
      )}
      {hasModule("social_scheduler") && (
        <div style={{ marginBottom: 16 }}>
          {!col && <SectionLabel>Social</SectionLabel>}
          <NavItem to="/social-scheduler" icon="📆" label="Scheduler" onClose={onClose} collapsed={col} />
        </div>
      )}
      {isSuperAdmin && (
        <div style={{ marginBottom: 16 }}>
          {!col && <SectionLabel>Admin</SectionLabel>}
          <NavItem to="/users" icon="🛡️" label="Usuarios" onClose={onClose} collapsed={col} />
          <NavItem to="/usage" icon="📈" label="Consumo API" onClose={onClose} collapsed={col} />
          <NavItem to="/settings" icon="⚙️" label="Configuración" onClose={onClose} collapsed={col} />
        </div>
      )}
    </>
  );

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* ── Desktop sidebar ── */}
      <aside className="sidebar-desktop" style={{
        width: collapsed ? 48 : 220,
        minWidth: collapsed ? 48 : 220,
        background: "var(--bg-surface)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          padding: "14px 12px", borderBottom: "1px solid var(--border)", minHeight: 56,
        }}>
          {!collapsed && (
            <img src={logoSrc} alt="Creativedog"
              style={{ width: 130, height: "auto", objectFit: "contain" }} />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-muted)", fontSize: 18, padding: 4, lineHeight: 1,
              flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "var(--radius-sm)", width: 28, height: 28,
            }}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        <nav style={{
          flex: 1, overflowY: "auto", overflowX: "hidden",
          padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2,
        }}>
          {navContent(null, collapsed)}
        </nav>

        <div style={{ borderTop: "1px solid var(--border)", padding: "10px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {user?.avatar && (
              <img src={user.avatar} alt={user.name}
                style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }} />
            )}
            {!collapsed && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {user?.name?.split(" ")[0]}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{roleLabel(user?.role)}</div>
                </div>
                <ThemeToggle theme={theme} onToggle={toggleTheme} size={26} />
                <button onClick={logout} title="Cerrar sesión" style={{
                  background: "none", border: "1px solid var(--border)", cursor: "pointer",
                  color: "var(--text-muted)", fontSize: 11, padding: "4px 8px",
                  borderRadius: "var(--radius-sm)", transition: "color 0.12s, border-color 0.12s, background 0.12s",
                  fontFamily: "inherit", flexShrink: 0,
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = "var(--danger)"; e.currentTarget.style.borderColor = "var(--danger)"; e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "none"; }}
                >
                  Salir
                </button>
              </>
            )}
            {collapsed && <ThemeToggle theme={theme} onToggle={toggleTheme} size={28} />}
          </div>
        </div>
      </aside>

      {/* ── Main column (contains mobile topbar + scrollable content) ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Mobile topbar — hidden on desktop via CSS */}
        <header className="mobile-topbar" style={{
          display: "none", /* shown via CSS on mobile */
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          height: 52,
          background: "var(--bg-surface)",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
          zIndex: 10,
        }}>
          <img src={logoSrc} alt="Creativedog" style={{ height: 26, objectFit: "contain" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ThemeToggle theme={theme} onToggle={toggleTheme} size={32} />
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--text-secondary)", fontSize: 22, padding: 4,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "var(--radius-sm)",
              }}
            >
              ☰
            </button>
          </div>
        </header>

        {/* Scrollable page content */}
        <main style={{ flex: 1, overflowY: "auto", background: "var(--bg-base)" }}>
          <div className="main-content-wrapper">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            zIndex: 500, display: "flex",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 280, background: "var(--bg-surface)",
              borderRight: "1px solid var(--border)",
              display: "flex", flexDirection: "column",
              animation: "slideInLeft 0.2s ease",
            }}
          >
            {/* Drawer header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 16px", borderBottom: "1px solid var(--border)", minHeight: 52,
            }}>
              <img src={logoSrc} alt="Creativedog" style={{ height: 26, objectFit: "contain" }} />
              <button
                onClick={() => setMobileOpen(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--text-muted)", fontSize: 20, padding: 4, lineHeight: 1,
                  display: "flex", alignItems: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Nav */}
            <nav style={{
              flex: 1, overflowY: "auto", padding: "12px 8px",
              display: "flex", flexDirection: "column", gap: 2,
            }}>
              {navContent(() => setMobileOpen(false))}
            </nav>

            {/* User footer */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {user?.avatar && (
                  <img src={user.avatar} alt={user.name}
                    style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user?.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{roleLabel(user?.role)}</div>
                </div>
                <button onClick={logout} style={{
                  background: "none", border: "1px solid var(--border)", cursor: "pointer",
                  color: "var(--text-muted)", fontSize: 12, padding: "5px 10px",
                  borderRadius: "var(--radius-sm)", fontFamily: "inherit",
                }}>
                  Salir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .sidebar-desktop { display: none !important; }
          .mobile-topbar { display: flex !important; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <span style={{
      display: "block", fontSize: 10, fontWeight: 600, letterSpacing: "0.8px",
      textTransform: "uppercase", color: "var(--text-muted)", padding: "0 8px", marginBottom: 4,
    }}>
      {children}
    </span>
  );
}

function ThemeToggle({ theme, onToggle, size = 28 }) {
  const isDark = theme === "dark";
  return (
    <button
      onClick={onToggle}
      title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
      style={{
        width: size, height: size, borderRadius: "var(--radius-sm)",
        background: "none", border: "1px solid var(--border)",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.5, flexShrink: 0,
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--bg-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "none"; }}
    >
      {isDark ? "☀️" : "🌙"}
    </button>
  );
}

function NavItem({ to, icon, label, end, onClose, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClose}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center",
        gap: collapsed ? 0 : 10,
        justifyContent: collapsed ? "center" : "flex-start",
        padding: collapsed ? "8px 0" : "8px 10px",
        borderRadius: "var(--radius-md)",
        color: isActive ? "var(--accent)" : "var(--text-secondary)",
        background: isActive ? "var(--accent-dim)" : "transparent",
        fontWeight: isActive ? 500 : 400, fontSize: 14,
        textDecoration: "none", whiteSpace: "nowrap", cursor: "pointer",
      })}
    >
      <span style={{ fontSize: 15, flexShrink: 0, width: 20, textAlign: "center" }}>{icon}</span>
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}
